import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseResponse, ILoadingOptions, isBaseResponse, LoadingImageHeader } from '../models';
import { HttpRequestOptions } from '../models/http-request-options';
import { ConfigService } from './config.service';
import { ToastService } from './toast.service';

export abstract class DataService {
  baseUrl: string;

  protected constructor(
    private readonly http: HttpClient,
    protected toastService: ToastService,
    protected configService: ConfigService,
    baseUrl: string,
  ) {
    this.baseUrl = `${baseUrl}/api`;
  }

  // This function returns an observable of type T in the form of JSON.
  // The advantage of an observable: if it changes then this will fire off the changed detection as the DOM has changed
  public get<T>(url: string, requestOptions?: HttpRequestOptions, loadingOptions?: ILoadingOptions): Observable<T> {
    let headers = new HttpHeaders();
    if (loadingOptions?.authorization) {
      headers = headers.append('Authorization', loadingOptions.authorization);
    }
    return this.http.get<T | BaseResponse<T>>(`${this.baseUrl}/${url}`, { headers })
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public getForText(url: string, requestOptions?: HttpRequestOptions): Observable<any> {
    return this.http.get<string | BaseResponse<string>>(`${this.baseUrl}/${url}`)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public put<T>(url: string, itemToUpdate: any, requestOptions?: HttpRequestOptions): Observable<T> {
    return this.http.put<T | BaseResponse<T>>(`${this.baseUrl}/${url}`, itemToUpdate)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public post<T>(url: string, body: any, loadingOptions?: ILoadingOptions, requestOptions?: HttpRequestOptions): Observable<T> {
    let headers = new HttpHeaders();
    if (loadingOptions?.loadingImage) {
      headers = headers.append(LoadingImageHeader, loadingOptions.loadingImage);
    }
    if (loadingOptions?.authorization) {
      headers = headers.append('Authorization', loadingOptions.authorization);
    }

    return this.http.post<T | BaseResponse<T>>(`${this.baseUrl}/${url}`, body, { headers })
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public postDirect<T>(url: string, body: any, requestOptions?: HttpRequestOptions): Observable<T> {
    return this.http.post<T | BaseResponse<T>>(`${url}`, body)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public postForText(url: string, body: any, requestOptions?: HttpRequestOptions): Observable<any> {
    return this.http.post<string | BaseResponse<string>>(`${this.baseUrl}/${url}`, body)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public patch<T>(url: string, body: any, requestOptions?: HttpRequestOptions): Observable<T> {
    return this.http.patch<T | BaseResponse<T>>(`${this.baseUrl}/${url}`, body)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public delete<T>(url: string, requestOptions?: HttpRequestOptions): Observable<T> {
    return this.http.delete<T | BaseResponse<T>>(`${this.baseUrl}/${url}`)
      .pipe(this.extractResult(url), this.handleError(requestOptions?.showErrorToast ?? true));
  }

  public getFile(url: string, fileType: string): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${url}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          return new Blob([res.body], { type: fileType });
        }),
      );
  }

  /**
   * Extracts the T value from a BaseResponse<T>, throwing an error if the response
   * contained an error.
   */
  private extractResult<T>(url: string) {
    return map((r: T | BaseResponse<T>): T => {
      // TEMPORARILY CHECK TO SEE IF THE RESPONSE IS A BASERESPONSE<T>
      // THIS IS ONLY HERE WHILE THE TRANSITIONS IS MADE TO MAKE CONTROLLERS
      // RETURN A BASERESPONSE.
      if (!isBaseResponse(r)) {
        // eslint-disable-next-line no-console
        console.warn(`[Dev] Request made to '${url}' did not return a BaseResponse.`);
        return r as T;
      }
      if (r.error)
        throw r.error;
      return r.result as T;
    });
  }

  /**
   * Handles an error in the Http pipeline. Shows a toast notification of the error
   * and rethrows the error message forwards.
   */
  private handleError<T>(showToast: boolean = true) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return catchError((err: any, caught: Observable<T>) => {
      // eslint-disable-next-line no-console
      console.error('Caught unsuccessful HTTP response: ', err);

      let message = 'An unknown error occurred.';

      // Check if err.error is a BaseResponseError
      if (isBaseResponse(err.error)) {
        message = (err.error as BaseResponse<T>).error?.message ?? 'An unknown error occurred.';
      }

      // Handling other error types, such as 401 Unauthorized.
      if (err instanceof HttpErrorResponse) {
        if (isBaseResponse((err as HttpErrorResponse).error))
          message = (err.error as BaseResponse<T>).error?.message ?? 'An unknown error occurred.';
        else
          message = `HTTP Error ${(err as HttpErrorResponse).status}`;
      }

      if (showToast) {
        this.toastService.danger(`An error occurred while trying to complete the request.\n\n${message}`);
      }

      return throwError(() => new Error(message));
    });
  }
}
